<!--
  ========================================================================
  FormularioLead — formulário de captura reutilizável.
  ========================================================================
  Integra com ActiveCampaign via JSONP e inclui:
    • Seletor de DDD com bandeiras + busca + auto-detecção por IP
    • Máscara automática de telefone por país
    • Validação completa (e-mail, telefone, checkbox obrigatório)
    • Traduções ES/PT/EN internas (podem ser sobrescritas via props)
    • Estados visuais de erro e aviso no checkbox

  Uso mínimo:
    <FormularioLead
      :active-campaign="{ endpoint, u, f, s, c, m, act, v, or }"
      idioma="pt"
      @submitted="onSubmitted"
    />

  Props principais:
    - idioma              : 'es' | 'pt' | 'en'  (default: 'es')
    - activeCampaign      : objeto com endpoint + campos hidden do AC
    - acFieldMap          : mapeamento dos campos customizados do AC
                            default: { profissao: 'field[1]', pais: 'field[2]' }
    - redirectTo          : objeto para router.push após sucesso (ou null)
    - mostrarHeader       : mostra badge + título + subtítulo
    - tituloBadge/Parte1/Destaque/subtitulo/textoBotao : overrides opcionais
    - profissoes          : lista custom (array ou objeto por idioma)
    - paisPadrao          : código ISO-2 (ex: 'MT') — DDD fixo, sem auto-IP

  Emite:
    - submitted           : (payload) após envio ao AC (antes do redirect)
  ========================================================================
-->

<template>
  <div class="form-wrapper">
    <div v-if="mostrarHeader" class="form-header">
      <div class="badge-pill destaque">{{ textos.badge }}</div>
      <h2 class="secao-titulo">
        {{ textos.tituloParte1 }}
        <span class="texto-gradiente">{{ textos.tituloDestaque }}</span>
      </h2>
      <p class="form-sub">{{ textos.subtitulo }}</p>
    </div>

    <form class="form-captura" @submit.prevent="handleSubmit">
      <div class="form-row">
        <div class="form-group">
          <label :for="`${uid}-nome`">{{ textos.nome }}</label>
          <input
            :id="`${uid}-nome`"
            v-model="form.nome"
            type="text"
            name="fullname"
            :placeholder="textos.nomePh"
            required
            autocomplete="name"
          />
        </div>
        <div class="form-group">
          <label :for="`${uid}-email`">{{ textos.email }}</label>
          <input
            :id="`${uid}-email`"
            v-model.trim="form.email"
            type="email"
            name="email"
            :placeholder="textos.emailPh"
            required
            autocomplete="email"
            spellcheck="false"
          />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label :for="`${uid}-telefone`">{{ textos.telefone }}</label>
          <div
            class="telefone-group"
            :class="{ 'is-open': dddAberto, 'is-detectando': detectandoPais }"
            ref="telefoneGroupRef"
          >
            <button
              type="button"
              class="ddd-trigger"
              @click="toggleDdd"
              :aria-expanded="dddAberto"
              :title="paisSelecionado.nome"
            >
              <span class="ddd-flag">{{ paisSelecionado.emoji }}</span>
              <span class="ddd-code">+{{ paisSelecionado.ddd }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ddd-chevron"><path d="m6 9 6 6 6-6"/></svg>
            </button>

            <input
              :id="`${uid}-telefone`"
              :value="form.telefone"
              @input="onTelefoneInput"
              @keypress="onTelefoneKeypress"
              @paste="onTelefoneInput"
              type="tel"
              name="phone"
              :placeholder="textos.telefonePh"
              required
              autocomplete="tel"
              class="telefone-input"
              inputmode="numeric"
              :maxlength="telefoneMaxLength"
            />

            <div v-if="dddAberto" class="ddd-dropdown">
              <div class="ddd-busca">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input
                  type="text"
                  v-model="buscaPais"
                  :placeholder="textos.buscarPais"
                  @click.stop
                />
              </div>
              <ul class="ddd-lista">
                <li
                  v-for="p in paisesFiltrados"
                  :key="p.code"
                  class="ddd-item"
                  :class="{ selecionado: p.code === paisSelecionado.code }"
                  @click="selecionarPais(p)"
                >
                  <span class="ddd-item-flag">{{ p.emoji }}</span>
                  <span class="ddd-item-nome">{{ p.nome }}</span>
                  <span class="ddd-item-code">+{{ p.ddd }}</span>
                </li>
                <li v-if="paisesFiltrados.length === 0" class="ddd-vazio">
                  {{ textos.semResultados }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label :for="`${uid}-profissao`">{{ textos.profissao }}</label>
          <select
            :id="`${uid}-profissao`"
            v-model="form.profissao"
            name="field[profession]"
            required
          >
            <option value="" disabled>{{ textos.profissaoPh }}</option>
            <option v-for="opt in profissoesAtuais" :key="opt" :value="opt">
              {{ opt }}
            </option>
          </select>
        </div>
      </div>

      <label
        class="form-checkbox"
        :class="{
          'is-checked': form.aceito,
          'has-warning': !form.aceito,
          'has-error': submitTentado && !form.aceito
        }"
      >
        <input
          type="checkbox"
          v-model="form.aceito"
          required
          class="form-checkbox-input"
        />
        <span class="form-checkbox-box" aria-hidden="true">
          <svg v-if="form.aceito" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </span>
        <span class="form-checkbox-texto">{{ textos.aceito }}</span>
      </label>

      <transition name="aviso-fade">
        <div v-if="!form.aceito" class="form-aviso" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span>{{ textos.aceitoAviso }}</span>
        </div>
      </transition>

      <button type="submit" class="botao-principal" :disabled="isSubmitting">
        <span v-if="!isSubmitting">{{ textos.botao }}</span>
        <span v-else class="loading-wrapper">
          <div class="spinner"></div>
          {{ textos.enviando }}
        </span>
        <svg v-if="!isSubmitting" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14"/>
          <path d="m12 5 7 7-7 7"/>
        </svg>
      </button>

      <p class="form-privacidade">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        {{ textos.privacidade }}
      </p>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { buscarCodigoPaisPorIp, codigoPaisFallbackLocal } from '@/composables/useIdiomaAuto'

const router = useRouter()

const props = defineProps({
  idioma: { type: String, default: 'es' },
  activeCampaign: {
    type: Object,
    required: true
  },
  acFieldMap: {
    type: Object,
    default: () => ({ profissao: 'field[1]', pais: 'field[2]' })
  },
  tituloBadge: { type: String, default: null },
  tituloParte1: { type: String, default: null },
  tituloDestaque: { type: String, default: null },
  subtitulo: { type: String, default: null },
  textoBotao: { type: String, default: null },
  profissoes: { type: [Array, Object], default: null },
  redirectTo: { type: Object, default: () => ({ name: 'oligoscan-gracias' }) },
  mostrarHeader: { type: Boolean, default: true },
  /* Código ISO-2 do país (ex: 'MT', 'BR'). Se definido, usa como DDD
     padrão e desliga a auto-detecção por IP. */
  paisPadrao: { type: String, default: null }
})

const emit = defineEmits(['submitted'])

// UID para evitar colisão de IDs caso haja mais de uma instância na página
const uid = `flead-${Math.random().toString(36).slice(2, 8)}`

// ============================================================
// TRADUÇÕES INTERNAS (podem ser sobrescritas via props)
// ============================================================
const i18n = {
  es: {
    badge: 'Solicitar información',
    tituloParte1: 'Habla con un consultor y',
    tituloDestaque: 'recibe tu propuesta',
    subtitulo: 'Completa los datos y en segundos nuestro equipo te contactará por WhatsApp con toda la información.',
    nome: 'Nombre completo', nomePh: 'Tu nombre',
    email: 'E-mail', emailPh: 'tu@email.com',
    telefone: 'WhatsApp', telefonePh: 'Tu número',
    buscarPais: 'Buscar país...', semResultados: 'Sin resultados',
    profissao: 'Profesión', profissaoPh: 'Selecciona tu área',
    aceito: 'Acepto recibir información sobre OligoCheck por WhatsApp y e-mail.',
    aceitoAviso: 'Para avanzar es necesario aceptar recibir la información. Marca la casilla para continuar.',
    botao: 'Quiero hablar con un consultor', enviando: 'Enviando...',
    privacidade: 'Tus datos están protegidos. No compartimos información con terceros.',
    profissoes: ['Médico/a', 'Nutricionista', 'Esteticista', 'Terapeuta / Naturópata', 'Farmacéutico/a', 'Dentista', 'Fisioterapeuta', 'Otro']
  },
  pt: {
    badge: 'Solicitar informações',
    tituloParte1: 'Fale com um consultor e',
    tituloDestaque: 'receba sua proposta',
    subtitulo: 'Preencha os dados e em segundos nossa equipe entrará em contato pelo WhatsApp com todas as informações.',
    nome: 'Nome completo', nomePh: 'Seu nome',
    email: 'E-mail', emailPh: 'seu@email.com',
    telefone: 'WhatsApp', telefonePh: 'Seu número',
    buscarPais: 'Buscar país...', semResultados: 'Sem resultados',
    profissao: 'Profissão', profissaoPh: 'Selecione sua área',
    aceito: 'Aceito receber informações sobre o OligoCheck por WhatsApp e e-mail.',
    aceitoAviso: 'Para avançar é necessário aceitar receber as informações. Marque a caixa para continuar.',
    botao: 'Quero falar com um consultor', enviando: 'Enviando...',
    privacidade: 'Seus dados estão protegidos. Não compartilhamos com terceiros.',
    profissoes: ['Médico(a)', 'Nutricionista', 'Esteticista', 'Terapeuta / Naturopata', 'Farmacêutico(a)', 'Dentista', 'Fisioterapeuta', 'Outro']
  },
  en: {
    badge: 'Request information',
    tituloParte1: 'Talk to a consultant and',
    tituloDestaque: 'get your proposal',
    subtitulo: 'Fill in your details and within seconds our team will reach out on WhatsApp with all the information.',
    nome: 'Full name', nomePh: 'Your name',
    email: 'Email', emailPh: 'your@email.com',
    telefone: 'WhatsApp', telefonePh: 'Your number',
    buscarPais: 'Search country...', semResultados: 'No results',
    profissao: 'Profession', profissaoPh: 'Select your area',
    aceito: 'I agree to receive information about OligoCheck via WhatsApp and email.',
    aceitoAviso: 'You must accept to receive information in order to proceed. Check the box to continue.',
    botao: 'I want to talk to a consultant', enviando: 'Sending...',
    privacidade: 'Your data is protected. We do not share information with third parties.',
    profissoes: ['Physician', 'Nutritionist', 'Esthetician', 'Therapist / Naturopath', 'Pharmacist', 'Dentist', 'Physiotherapist', 'Other']
  }
}

// Textos finais (aplica overrides das props)
const textos = computed(() => {
  const base = i18n[props.idioma] || i18n.es
  return {
    ...base,
    badge: props.tituloBadge || base.badge,
    tituloParte1: props.tituloParte1 || base.tituloParte1,
    tituloDestaque: props.tituloDestaque || base.tituloDestaque,
    subtitulo: props.subtitulo || base.subtitulo,
    botao: props.textoBotao || base.botao
  }
})

const profissoesAtuais = computed(() => {
  if (Array.isArray(props.profissoes)) return props.profissoes
  if (props.profissoes && typeof props.profissoes === 'object') {
    return props.profissoes[props.idioma] || props.profissoes.es || []
  }
  const base = i18n[props.idioma] || i18n.es
  return base.profissoes
})

// ============================================================
// LISTA DE PAÍSES (DDD) + máscaras de telefone
// '#' representa dígito; qualquer outro caractere é literal.
// ============================================================
const paises = [
  { code: 'ES', nome: 'España',              ddd: '34',  emoji: '🇪🇸', format: '### ## ## ##' },
  { code: 'PT', nome: 'Portugal',            ddd: '351', emoji: '🇵🇹', format: '### ### ###' },
  { code: 'MT', nome: 'Malta',               ddd: '356', emoji: '🇲🇹', format: '#### ####' },
  { code: 'BR', nome: 'Brasil',              ddd: '55',  emoji: '🇧🇷', format: '(##) #####-####' },
  { code: 'MX', nome: 'México',              ddd: '52',  emoji: '🇲🇽', format: '## #### ####' },
  { code: 'AR', nome: 'Argentina',           ddd: '54',  emoji: '🇦🇷', format: '## #### ####' },
  { code: 'CL', nome: 'Chile',               ddd: '56',  emoji: '🇨🇱', format: '# #### ####' },
  { code: 'CO', nome: 'Colombia',            ddd: '57',  emoji: '🇨🇴', format: '### ### ####' },
  { code: 'PE', nome: 'Perú',                ddd: '51',  emoji: '🇵🇪', format: '### ### ###' },
  { code: 'UY', nome: 'Uruguay',             ddd: '598', emoji: '🇺🇾', format: '## ### ###' },
  { code: 'PY', nome: 'Paraguay',            ddd: '595', emoji: '🇵🇾', format: '### ### ###' },
  { code: 'BO', nome: 'Bolivia',             ddd: '591', emoji: '🇧🇴', format: '#### ####' },
  { code: 'EC', nome: 'Ecuador',             ddd: '593', emoji: '🇪🇨', format: '## ### ####' },
  { code: 'VE', nome: 'Venezuela',           ddd: '58',  emoji: '🇻🇪', format: '### ### ####' },
  { code: 'CR', nome: 'Costa Rica',          ddd: '506', emoji: '🇨🇷', format: '#### ####' },
  { code: 'PA', nome: 'Panamá',              ddd: '507', emoji: '🇵🇦', format: '#### ####' },
  { code: 'GT', nome: 'Guatemala',           ddd: '502', emoji: '🇬🇹', format: '#### ####' },
  { code: 'SV', nome: 'El Salvador',         ddd: '503', emoji: '🇸🇻', format: '#### ####' },
  { code: 'HN', nome: 'Honduras',            ddd: '504', emoji: '🇭🇳', format: '#### ####' },
  { code: 'NI', nome: 'Nicaragua',           ddd: '505', emoji: '🇳🇮', format: '#### ####' },
  { code: 'DO', nome: 'Rep. Dominicana',     ddd: '1',   emoji: '🇩🇴', format: '(###) ###-####' },
  { code: 'PR', nome: 'Puerto Rico',         ddd: '1',   emoji: '🇵🇷', format: '(###) ###-####' },
  { code: 'CU', nome: 'Cuba',                ddd: '53',  emoji: '🇨🇺', format: '#### ####' },
  { code: 'US', nome: 'Estados Unidos',      ddd: '1',   emoji: '🇺🇸', format: '(###) ###-####' },
  { code: 'CA', nome: 'Canadá',              ddd: '1',   emoji: '🇨🇦', format: '(###) ###-####' },
  { code: 'GB', nome: 'Reino Unido',         ddd: '44',  emoji: '🇬🇧', format: '#### ######' },
  { code: 'FR', nome: 'Francia',             ddd: '33',  emoji: '🇫🇷', format: '# ## ## ## ##' },
  { code: 'IT', nome: 'Italia',              ddd: '39',  emoji: '🇮🇹', format: '### ### ####' },
  { code: 'DE', nome: 'Alemania',            ddd: '49',  emoji: '🇩🇪', format: '### #######' },
  { code: 'NL', nome: 'Países Bajos',        ddd: '31',  emoji: '🇳🇱', format: '# ########' },
  { code: 'BE', nome: 'Bélgica',             ddd: '32',  emoji: '🇧🇪', format: '### ## ## ##' },
  { code: 'CH', nome: 'Suiza',               ddd: '41',  emoji: '🇨🇭', format: '## ### ## ##' },
  { code: 'AT', nome: 'Austria',             ddd: '43',  emoji: '🇦🇹', format: '### #######' },
  { code: 'IE', nome: 'Irlanda',             ddd: '353', emoji: '🇮🇪', format: '## ### ####' },
  { code: 'AD', nome: 'Andorra',             ddd: '376', emoji: '🇦🇩', format: '### ###' },
  { code: 'MA', nome: 'Marruecos',           ddd: '212', emoji: '🇲🇦', format: '### ### ###' },
  { code: 'AO', nome: 'Angola',              ddd: '244', emoji: '🇦🇴', format: '### ### ###' }
]

const formatarTelefone = (valor, mascara) => {
  const digitos = String(valor || '').replace(/\D/g, '')
  if (!mascara) return digitos
  let resultado = ''
  let i = 0
  for (const char of mascara) {
    if (i >= digitos.length) break
    if (char === '#') {
      resultado += digitos[i]
      i++
    } else {
      resultado += char
    }
  }
  return resultado
}

// ============================================================
// ESTADO
// ============================================================
const form = reactive({
  nome: '',
  email: '',
  telefone: '',
  profissao: '',
  aceito: true
})

const isSubmitting = ref(false)
const submitTentado = ref(false)

/* Pré-seleciona: prop paisPadrao → timezone/locale → Espanha. */
const paisPorCodigo = (codigo) => (codigo ? paises.find(p => p.code === String(codigo).toUpperCase()) : null)
const paisSelecionado = ref(
  paisPorCodigo(props.paisPadrao) ||
  paisPorCodigo(codigoPaisFallbackLocal()) ||
  paises[0]
)
const dddAberto = ref(false)
const buscaPais = ref('')
const detectandoPais = ref(!props.paisPadrao)
const telefoneGroupRef = ref(null)
/* Se o usuário trocar o DDD manualmente, a detecção por IP não sobrescreve. */
const paisEscolhidoManual = ref(false)

const paisesFiltrados = computed(() => {
  const q = buscaPais.value.trim().toLowerCase()
  if (!q) return paises
  return paises.filter(p =>
    p.nome.toLowerCase().includes(q) ||
    p.ddd.includes(q) ||
    p.code.toLowerCase().includes(q)
  )
})

const selecionarPais = (pais) => {
  paisEscolhidoManual.value = true
  paisSelecionado.value = pais
  dddAberto.value = false
  buscaPais.value = ''
  if (form.telefone) {
    form.telefone = formatarTelefone(form.telefone, pais.format)
  }
}

const onTelefoneInput = (e) => {
  form.telefone = formatarTelefone(e.target.value, paisSelecionado.value.format)
}

const onTelefoneKeypress = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return
  if (e.key && e.key.length === 1 && !/[0-9]/.test(e.key)) {
    e.preventDefault()
  }
}

const telefoneMaxLength = computed(() => {
  const mascara = paisSelecionado.value?.format
  return mascara ? mascara.length : 20
})

const telefoneDigitosCount = computed(() => {
  const mascara = paisSelecionado.value?.format || ''
  return (mascara.match(/#/g) || []).length
})

const toggleDdd = () => {
  dddAberto.value = !dddAberto.value
  if (dddAberto.value) {
    nextTick(() => {
      const input = telefoneGroupRef.value?.querySelector('.ddd-busca input')
      if (input) input.focus()
    })
  }
}

const handleClickFora = (e) => {
  if (!telefoneGroupRef.value) return
  if (!telefoneGroupRef.value.contains(e.target)) {
    dddAberto.value = false
  }
}

// ============================================================
// AUTO-DETECÇÃO DE PAÍS POR IP (reutiliza composable global)
// ============================================================
const detectarPaisPorIp = async () => {
  /* Página com DDD fixo (ex: evento Malta) — não sobrescreve. */
  if (props.paisPadrao) {
    detectandoPais.value = false
    return
  }
  try {
    const codigo = await buscarCodigoPaisPorIp()
    if (paisEscolhidoManual.value) return
    if (codigo) {
      const encontrado = paisPorCodigo(codigo)
      if (encontrado) {
        paisSelecionado.value = encontrado
        if (form.telefone) {
          form.telefone = formatarTelefone(form.telefone, encontrado.format)
        }
      }
    }
  } catch (e) {
    console.warn('[FormularioLead] falha ao detectar país por IP:', e)
  } finally {
    detectandoPais.value = false
  }
}

// ============================================================
// VALIDAÇÃO
// ============================================================
const emailValido = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim())
}

const telefoneCompleto = computed(() => {
  const numero = (form.telefone || '').replace(/\D/g, '')
  return `+${paisSelecionado.value.ddd}${numero}`
})

// ============================================================
// INTEGRAÇÃO ACTIVECAMPAIGN (JSONP)
//
// Estratégia anti-duplicação:
//   1. Atributo `data-ac-jsonp` nas tags <script> criadas — permite
//      limpar scripts antigos (HMR, múltiplas submissões na mesma SPA
//      session) antes de inserir um novo.
//   2. Guarda baseada em email+formId: se exatamente o mesmo par foi
//      submetido nos últimos 30s, bloqueia a segunda tentativa.
//   3. Log explícito em dev com a URL final — facilita conferir na
//      aba Network se há 1 ou N requests ao endpoint AC.
// ============================================================
const AC_DEDUP_WINDOW_MS = 30_000
const acSubmitLock = new Map() // chave: `${u}|${email}` → timestamp

const submitToActiveCampaign = () => {
  return new Promise((resolve) => {
    const ac = props.activeCampaign || {}
    const endpoint = ac.endpoint
    if (!endpoint) { resolve(); return }

    /* Guard de deduplicação por (formId + email). Protege contra
       double-click, HMR e submissões sucessivas com mesmo email. */
    const formKey = `${ac.u || 'x'}|${form.email.trim().toLowerCase()}`
    const agora = Date.now()
    const ultimoEnvio = acSubmitLock.get(formKey)
    if (ultimoEnvio && (agora - ultimoEnvio) < AC_DEDUP_WINDOW_MS) {
      console.warn('[FormularioLead] submissão duplicada ignorada (guard de 30s):', formKey)
      resolve()
      return
    }
    acSubmitLock.set(formKey, agora)

    /* Remove qualquer <script> JSONP residual de submissões anteriores
       (mesmo formulário ou outros). Evita acúmulo no <head> sem efeito
       funcional mas útil para debug. */
    document.head
      .querySelectorAll('script[data-ac-jsonp="true"]')
      .forEach(s => s.remove())

    let finalizado = false
    const finalizar = () => {
      if (finalizado) return
      finalizado = true
      window._show_thank_you = prevThankYou
      window._show_error = prevError
      resolve()
    }

    const prevThankYou = window._show_thank_you
    const prevError = window._show_error

    window._show_thank_you = function () {
      try { if (prevThankYou) prevThankYou.apply(this, arguments) } catch (_) {}
      finalizar()
    }
    window._show_error = function () {
      try { if (prevError) prevError.apply(this, arguments) } catch (_) {}
      finalizar()
    }
    if (!window._show_unsubscribe) window._show_unsubscribe = function () {}
    if (!window._show_pc_confirmation) window._show_pc_confirmation = function () {}

    const telefoneFormatado = `+${paisSelecionado.value.ddd} ${form.telefone}`.trim()

    /* Hidden fields do AC. Só passa os que vêm na prop — evita enviar
       IDs vazios (ex: `s=`) que podem confundir automations do AC. */
    const hidden = {}
    const hiddenKeys = ['u', 'f', 's', 'c', 'm', 'act', 'v', 'or']
    hiddenKeys.forEach(k => {
      const val = ac[k]
      if (val !== undefined && val !== null && val !== '') {
        hidden[k] = String(val)
      }
    })
    if (!hidden.act) hidden.act = 'sub'
    if (!hidden.v) hidden.v = '2'

    const params = new URLSearchParams({
      ...hidden,
      fullname: form.nome.trim(),
      email: form.email.trim(),
      phone: telefoneFormatado,
      [props.acFieldMap.profissao || 'field[1]']: form.profissao,
      [props.acFieldMap.pais || 'field[2]']: paisSelecionado.value.nome,
      jsonp: 'true'
    })

    const finalUrl = `${endpoint}?${params.toString()}`

    /* Log em dev/preview ajuda a conferir na aba Network que só UM
       request é disparado e para qual form (u/f). Em produção o
       import.meta.env.DEV é false e nada é logado. */
    if (import.meta.env?.DEV) {
      console.info(`[FormularioLead] ActiveCampaign JSONP → form u=${hidden.u} f=${hidden.f}`)
      console.info(`[FormularioLead] URL:`, finalUrl)
    }

    const script = document.createElement('script')
    script.src = finalUrl
    script.async = true
    script.setAttribute('data-ac-jsonp', 'true')
    script.setAttribute('data-ac-form', hidden.u || '')
    script.onerror = () => finalizar()
    document.head.appendChild(script)

    // Fallback: se o AC não responder em 5s, segue o fluxo mesmo assim
    setTimeout(finalizar, 5000)
  })
}

// ============================================================
// SUBMIT
// ============================================================
const handleSubmit = async () => {
  if (isSubmitting.value) return

  submitTentado.value = true

  if (
    !form.nome.trim() ||
    !form.email ||
    !form.telefone ||
    !form.profissao ||
    !form.aceito
  ) return

  if (!emailValido(form.email)) return

  const digitosTel = (form.telefone || '').replace(/\D/g, '')
  const minimoDigitos = Math.max(6, telefoneDigitosCount.value - 1)
  if (digitosTel.length < minimoDigitos) return

  isSubmitting.value = true

  try {
    await submitToActiveCampaign()

    const payload = {
      nome: form.nome.trim(),
      primeiroNome: form.nome.trim().split(' ')[0] || '',
      email: form.email.trim(),
      telefone: form.telefone,
      telefoneCompleto: telefoneCompleto.value,
      profissao: form.profissao,
      pais: { ...paisSelecionado.value },
      idioma: props.idioma
    }

    emit('submitted', payload)

    if (props.redirectTo) {
      router.push({
        ...props.redirectTo,
        query: {
          lang: props.idioma,
          nome: payload.primeiroNome,
          tel: payload.telefoneCompleto,
          ...(props.redirectTo.query || {})
        }
      })
    } else {
      isSubmitting.value = false
    }
  } catch (e) {
    console.error('[FormularioLead] erro ao enviar:', e)
    isSubmitting.value = false
  }
}

// ============================================================
// LIFECYCLE
// ============================================================
onMounted(() => {
  detectarPaisPorIp()
  document.addEventListener('click', handleClickFora)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickFora)
})

// Ao trocar de idioma, limpa profissão se não existir mais na lista nova
watch(() => props.idioma, () => {
  if (form.profissao && !profissoesAtuais.value.includes(form.profissao)) {
    form.profissao = ''
  }
})
</script>

<style scoped>
.form-wrapper {
  max-width: 640px;
  margin: 0 auto;
  background: rgba(10, 26, 42, 0.6);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  padding: 40px 32px;
  box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.6);
  position: relative;
  z-index: 1;
  color: #fff;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: var(--bold);
  font-size: 11px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 14px;
}

.badge-pill.destaque {
  color: var(--cor-azul);
  background: rgba(20, 203, 240, 0.08);
  border-color: rgba(20, 203, 240, 0.25);
}

.form-header .secao-titulo {
  font-family: var(--estilo);
  font-size: 28px;
  margin: 0 0 12px;
  color: #fff;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.texto-gradiente {
  background: linear-gradient(135deg, var(--cor-azul), var(--cor-ciano));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.form-sub {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.5;
  margin: 0;
}

.form-captura {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-family: var(--bold);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
}

.form-group input,
.form-group select {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 13px 16px;
  color: #fff;
  font-family: var(--regular);
  font-size: 15px;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.form-group input:focus,
.form-group select:focus {
  border-color: var(--cor-azul);
  background: rgba(20, 203, 240, 0.05);
  box-shadow: 0 0 0 3px rgba(20, 203, 240, 0.15);
}

.form-group select {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ffffff80' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
}

.form-group select option {
  background: #0a1a2a;
  color: #fff;
}

/* ============================================
   SELETOR DE DDD (TELEFONE)
   ============================================ */
.telefone-group {
  position: relative;
  display: flex;
  align-items: stretch;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: all 0.2s ease;
  overflow: visible;
}

.telefone-group:focus-within {
  border-color: var(--cor-azul);
  background: rgba(20, 203, 240, 0.05);
  box-shadow: 0 0 0 3px rgba(20, 203, 240, 0.15);
}

.telefone-group.is-open {
  border-color: var(--cor-azul);
  box-shadow: 0 0 0 3px rgba(20, 203, 240, 0.15);
}

.telefone-group.is-detectando .ddd-trigger {
  opacity: 0.6;
}

.ddd-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0 12px 0 14px;
  color: #fff;
  font-family: var(--regular);
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;
  border-radius: 12px 0 0 12px;
  min-height: 100%;
}

.ddd-trigger:hover { background: rgba(255, 255, 255, 0.04); }

.ddd-trigger:focus-visible {
  outline: none;
  background: rgba(20, 203, 240, 0.06);
}

.ddd-flag {
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.ddd-code {
  font-family: var(--bold);
  color: #fff;
  font-size: 14px;
  letter-spacing: 0.3px;
}

.ddd-chevron {
  color: rgba(255, 255, 255, 0.45);
  transition: transform 0.25s ease;
  margin-left: 2px;
}

.telefone-group.is-open .ddd-chevron {
  transform: rotate(180deg);
  color: var(--cor-azul);
}

.telefone-input {
  flex: 1;
  min-width: 0;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 13px 14px !important;
  color: #fff;
  font-family: var(--regular);
  font-size: 15px;
  outline: none;
}

.telefone-input:focus {
  background: transparent !important;
  box-shadow: none !important;
}

.telefone-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.ddd-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: rgba(10, 26, 42, 0.95);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 20px 50px -10px rgba(0, 0, 0, 0.7);
  z-index: 50;
  overflow: hidden;
  animation: dropdownIn 0.18s ease-out;
}

@keyframes dropdownIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.ddd-busca {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.ddd-busca:focus-within {
  border-color: var(--cor-azul);
  background: rgba(20, 203, 240, 0.05);
  box-shadow: 0 0 0 2px rgba(20, 203, 240, 0.12);
}

.ddd-busca svg {
  color: rgba(255, 255, 255, 0.45);
  flex-shrink: 0;
}

.form-group .ddd-busca input,
.form-group .ddd-busca input:focus {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  color: #fff;
  font-family: var(--regular);
  font-size: 13px;
  padding: 2px 0;
  border-radius: 0;
  min-width: 0;
  width: auto;
}

.form-group .ddd-busca input::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.ddd-lista {
  list-style: none;
  padding: 4px 6px 8px;
  margin: 0;
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
}

.ddd-lista::-webkit-scrollbar { width: 6px; }
.ddd-lista::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.ddd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
}

.ddd-item:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

.ddd-item.selecionado {
  background: rgba(20, 203, 240, 0.08);
  border-color: rgba(20, 203, 240, 0.25);
}

.ddd-item-flag {
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
}

.ddd-item-nome {
  flex: 1;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-family: var(--regular);
}

.ddd-item-code {
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
  font-family: var(--bold);
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.ddd-item.selecionado .ddd-item-nome { color: #fff; }
.ddd-item.selecionado .ddd-item-code { color: var(--cor-azul); }

.ddd-vazio {
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  padding: 20px 12px;
}

/* ============================================
   CHECKBOX DE ACEITE
   ============================================ */
.form-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
  cursor: pointer;
  margin-top: 4px;
  position: relative;
}

.form-checkbox-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.form-checkbox-box {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: transparent;
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.form-checkbox:hover .form-checkbox-box {
  border-color: rgba(255, 255, 255, 0.45);
}

.form-checkbox.is-checked .form-checkbox-box {
  background: var(--cor-azul);
  border-color: var(--cor-azul);
}

.form-checkbox-input:focus-visible + .form-checkbox-box {
  box-shadow: 0 0 0 2px rgba(20, 203, 240, 0.35);
}

.form-checkbox.has-warning .form-checkbox-box {
  border-color: #f5a623;
  box-shadow: 0 0 0 2px rgba(245, 166, 35, 0.18);
}

.form-checkbox.has-error .form-checkbox-box {
  border-color: #F37C7B;
  box-shadow: 0 0 0 2px rgba(243, 124, 123, 0.22);
  animation: shakeX 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

.form-checkbox.has-error .form-checkbox-texto { color: #F37C7B; }

@keyframes shakeX {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-3px); }
  40%, 80% { transform: translateX(3px); }
}

.form-checkbox-texto { flex: 1; }

.form-aviso {
  margin: -4px 0 0;
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 1.45;
  color: #ffb55a;
  background: rgba(245, 166, 35, 0.08);
  border: 1px solid rgba(245, 166, 35, 0.3);
  border-radius: 10px;
}

.form-aviso svg {
  flex-shrink: 0;
  color: #f5a623;
  margin-top: 1px;
}

.aviso-fade-enter-active,
.aviso-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease, max-height 0.25s ease;
  overflow: hidden;
}

.aviso-fade-enter-from,
.aviso-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  border-width: 0;
}

.aviso-fade-enter-to,
.aviso-fade-leave-from {
  opacity: 1;
  max-height: 80px;
}

/* ============================================
   BOTÃO PRINCIPAL
   ============================================ */
.botao-principal {
  background: linear-gradient(135deg, #f58220, #e56a00);
  color: #fff;
  border: none;
  padding: 16px 28px;
  border-radius: 14px;
  font-family: var(--bold);
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 24px -4px rgba(245, 130, 32, 0.45);
  cursor: pointer;
  margin-top: 8px;
  width: 100%;
}

.botao-principal:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px -6px rgba(245, 130, 32, 0.6);
  filter: brightness(1.05);
}

.botao-principal:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.form-privacidade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  margin: 8px 0 0;
  text-align: center;
}

.form-privacidade svg {
  color: var(--cor-ciano);
  flex-shrink: 0;
}

/* ============================================
   RESPONSIVO
   ============================================ */
@media (max-width: 720px) {
  .form-wrapper { padding: 32px 22px; border-radius: 22px; }
  .form-header .secao-titulo { font-size: 24px; }
  .form-row { grid-template-columns: 1fr; gap: 16px; }
}
</style>
